import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/movies';
import type { MovieItem } from '@/mocks/movies';

export default function HeroCarousel() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const goToNext = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  }, []);

  const goToPrev = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(goToNext, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, goToNext]);

  const slide = heroSlides[currentSlide];

  return (
    <section
      className="relative w-full h-[420px] md:h-[600px] overflow-hidden"
      onMouseEnter={() => setIsAutoPlaying(false)}
      onMouseLeave={() => setIsAutoPlaying(true)}
      aria-label="精选推荐轮播"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={slide.bannerImage}
          alt={slide.title}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-4 md:px-6 flex items-center">
        <div className="w-full flex flex-col md:flex-row items-center gap-8 md:gap-16">
          {/* Cover */}
          <div className="hidden md:block flex-shrink-0">
            <img
              src={slide.coverImage}
              alt={slide.title}
              className="w-48 lg:w-56 rounded-xl shadow-2xl object-cover"
              style={{ aspectRatio: '2/3' }}
            />
          </div>

          {/* Info */}
          <div className="flex-1 text-center md:text-left">
            <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
              <span className="px-3 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                {slide.category}
              </span>
              {slide.isHot && (
                <span className="px-3 py-1 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                  热门推荐
                </span>
              )}
              {slide.isNew && (
                <span className="px-3 py-1 bg-foreground-800 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                  最新上线
                </span>
              )}
            </div>

            <h2 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-background-50 mb-3">
              {slide.title}
            </h2>
            <p className="text-sm md:text-base text-background-200 mb-2">
              {slide.englishTitle}
            </p>

            <div className="flex items-center justify-center md:justify-start gap-4 mb-4 text-sm text-background-200">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-accent-400"></i>
                <strong className="text-background-50">{slide.rating}</strong>
              </span>
              <span>{slide.year}</span>
              <span>{slide.region}</span>
              <span>{slide.duration}</span>
            </div>

            <p className="text-sm text-background-200/80 leading-relaxed mb-6 max-w-2xl line-clamp-2">
              {slide.description}
            </p>

            <div className="flex items-center justify-center md:justify-start gap-3">
              <Link
                to={`/article/${slide.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-play-circle-line text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/article/${slide.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-background-50/15 hover:bg-background-50/25 text-background-50 font-medium rounded-lg backdrop-blur-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-information-line text-lg"></i>
                详情介绍
              </Link>
            </div>

            <p className="mt-4 text-xs text-background-400">
              {slide.updateEpisode}
            </p>
          </div>
        </div>
      </div>

      {/* Arrows */}
      <button
        onClick={goToPrev}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/15 hover:bg-background-50/30 backdrop-blur-sm text-background-50 transition-all duration-300 cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={goToNext}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/15 hover:bg-background-50/30 backdrop-blur-sm text-background-50 transition-all duration-300 cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      {/* Dots */}
      <div className="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-2">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
              index === currentSlide
                ? 'w-8 bg-primary-500'
                : 'w-2 bg-background-50/40 hover:bg-background-50/60'
            }`}
            aria-label={`第${index + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}